import React, { useCallback, useEffect, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { EmblaCarouselType } from 'embla-carousel'
import { Stack, IconButton, IconButtonProps } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'

import { Text } from '@trivie/ui-web/components/text'
import { PlayTile } from '@trivie/ui-mobile/components/play-tile'
import { color } from '@trivie/ui-web/theme'

export const UpNextList = (props: any) => {
  const { onTilePress, locale, tiles, options, style } = props
  const [emblaRef, emblaApi] = useEmblaCarousel(options)

  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaApi)

  return (
    <Stack style={style}>
      <Stack direction="row" justifyContent="space-between" mb={1} alignItems="center">
        <Text tx="Quiz.upNext" variant="body1Emphasized" style={{ color: color.shade70 }} />
        <Stack direction="row" justifyContent="flex-end" useFlexGap gap={1}>
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
        </Stack>
      </Stack>
      <div style={{ overflow: 'hidden' }} ref={emblaRef}>
        <div style={{ display: 'flex' }}>
          {tiles.map((tile, index) => (
            <PlayTile
              key={`upnext_tile_${index}`}
              index={index}
              style={{ marginRight: 20 }}
              onPress={() => onTilePress(tile)}
              tile={tile}
              locale={locale}
            />
          ))}
        </div>
      </div>
    </Stack>
  )
}

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean
  nextBtnDisabled: boolean
  onPrevButtonClick: () => void
  onNextButtonClick: () => void
}

export const usePrevNextButtons = (
  embla: EmblaCarouselType | undefined,
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const onPrevButtonClick = useCallback(() => {
    if (!embla) return
    embla.scrollPrev()
  }, [embla])

  const onNextButtonClick = useCallback(() => {
    if (!embla) return
    embla.scrollNext()
  }, [embla])

  const onSelect = useCallback((emb: EmblaCarouselType) => {
    setPrevBtnDisabled(!emb.canScrollPrev())
    setNextBtnDisabled(!emb.canScrollNext())
  }, [])

  useEffect(() => {
    if (!embla) return

    onSelect(embla)
    embla.on('reInit', onSelect).on('select', onSelect)
  }, [embla, onSelect])

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  }
}

const PrevButton = (props: IconButtonProps) => {
  const { ...restProps } = props

  return (
    <IconButton data-qat="previous_btn" {...restProps}>
      <ChevronLeft />
    </IconButton>
  )
}

const NextButton = (props: IconButtonProps) => {
  const { ...restProps } = props

  return (
    <IconButton data-qat="next_btn" {...restProps}>
      <ChevronRight />
    </IconButton>
  )
}
